import { useState, useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  useDeliverablesDashboard,
  useUpdateDeliverable,
  useDeleteDeliverable,
  type DeliverableDashboardFilters,
} from '@/hooks/useDeliverables';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { DeliverableRow } from '@/components/deliverables/DeliverableRow';
import { isStale, isOverdue } from '@/components/deliverables/deliverable-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Deliverable, DeliverableStatus } from '@/hooks/useDeliverables';
import { useNavigate } from 'react-router-dom';

interface MyWorkViewProps {
  personName: string;
  description?: string;
  filters: DeliverableDashboardFilters;
}

export function MyWorkView({ personName, description, filters }: MyWorkViewProps) {
  const [search, setSearch] = useState('');
  const mergedFilters = useMemo(
    () => ({ ...filters, ...(search ? { search } : {}) }),
    [filters, search]
  );
  const { data, isLoading } = useDeliverablesDashboard(mergedFilters);
  const { data: teamMembers } = useTeamMembers();
  const updateDeliverable = useUpdateDeliverable();
  const deleteDeliverable = useDeleteDeliverable();
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const deliverables = data?.deliverables ?? [];

  // Bucket deliverables into priority sections
  const { attention, inProgress, inReview, pending, completed } = useMemo(() => {
    const att: (Deliverable & { project_name?: string })[] = [];
    const prog: (Deliverable & { project_name?: string })[] = [];
    const rev: (Deliverable & { project_name?: string })[] = [];
    const pend: (Deliverable & { project_name?: string })[] = [];
    const done: (Deliverable & { project_name?: string })[] = [];

    for (const d of deliverables) {
      if (d.status === 'approved' || d.status === 'delivered') {
        done.push(d);
      } else if (isStale(d) || isOverdue(d)) {
        att.push(d);
      } else if (d.status === 'in_progress') {
        prog.push(d);
      } else if (d.status === 'in_review' || d.status === 'revision') {
        rev.push(d);
      } else {
        pend.push(d);
      }
    }

    // Sort attention items by staleness (most stale first)
    att.sort((a, b) => {
      const aTime = a.status_changed_at ? new Date(a.status_changed_at).getTime() : Infinity;
      const bTime = b.status_changed_at ? new Date(b.status_changed_at).getTime() : Infinity;
      return aTime - bTime;
    });

    return { attention: att, inProgress: prog, inReview: rev, pending: pend, completed: done };
  }, [deliverables]);

  const activeCount = deliverables.length - completed.length;

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>;
  }

  if (!data || deliverables.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-muted-foreground">No deliverables found for {personName}.</p>
      </div>
    );
  }

  const rowProps = (d: Deliverable & { project_name?: string }) => ({
    deliverable: d,
    expandedId,
    onToggle: () => setExpandedId(expandedId === d.id ? null : d.id),
    onStatusChange: (id: string, status: DeliverableStatus) => updateDeliverable.mutateAsync({ id, data: { status } }),
    onDelete: setDeleteId,
    onNavigate: navigate,
    teamMembers,
    onUpdate: (id: string, data: any) => updateDeliverable.mutateAsync({ id, data }),
    compact: false,
    showProject: true,
  });

  return (
    <div className="space-y-6 min-w-0">
      {/* Search */}
      <FilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search deliverables..."
        onClearAll={() => setSearch('')}
        className="rounded-lg border bg-card p-3"
      />

      {/* Summary bar */}
      <div className="flex items-center gap-3 text-sm">
        {attention.length > 0 && (
          <span className="text-red-600 font-medium">{attention.length} need attention</span>
        )}
        <span className="text-blue-600">{activeCount} active</span>
        <span className="text-muted-foreground">{completed.length} complete</span>
      </div>

      {/* Needs Attention */}
      {attention.length > 0 && (
        <Section
          label="Needs Attention"
          count={attention.length}
          variant="attention"
        >
          <div className="space-y-1">
            {attention.map(d => (
              <DeliverableRow key={d.id} {...rowProps(d)} />
            ))}
          </div>
        </Section>
      )}

      {/* In Progress */}
      {inProgress.length > 0 && (
        <Section label="In Progress" count={inProgress.length}>
          <div className="space-y-1">
            {inProgress.map(d => (
              <DeliverableRow key={d.id} {...rowProps(d)} />
            ))}
          </div>
        </Section>
      )}

      {/* In Review */}
      {inReview.length > 0 && (
        <Section label="In Review" count={inReview.length}>
          <div className="space-y-1">
            {inReview.map(d => (
              <DeliverableRow key={d.id} {...rowProps(d)} />
            ))}
          </div>
        </Section>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <Section label="Pending" count={pending.length}>
          <div className="space-y-1">
            {pending.map(d => (
              <DeliverableRow key={d.id} {...rowProps(d)} />
            ))}
          </div>
        </Section>
      )}

      {/* Completed — collapsed by default */}
      {completed.length > 0 && (
        <div>
          <button
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
            onClick={() => setShowCompleted(!showCompleted)}
          >
            {showCompleted
              ? <ChevronDown className="h-4 w-4" />
              : <ChevronRight className="h-4 w-4" />
            }
            <span className="font-medium">Completed</span>
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted">{completed.length}</span>
          </button>
          {showCompleted && (
            <div className="space-y-1 opacity-60">
              {completed.map(d => (
                <DeliverableRow key={d.id} {...rowProps(d)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete deliverable?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const target = deliverables.find(d => d.id === deleteId);
                return target
                  ? <>This will permanently remove <span className="font-medium text-foreground">"{target.title}"</span>. This action cannot be undone.</>
                  : 'This will permanently remove this deliverable. This action cannot be undone.';
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteId) return;
                await deleteDeliverable.mutateAsync(deleteId);
                setDeleteId(null);
                if (expandedId === deleteId) setExpandedId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({
  label,
  count,
  variant,
  children,
}: {
  label: string;
  count: number;
  variant?: 'attention';
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {variant === 'attention' && <AlertTriangle className="h-4 w-4 text-red-600" />}
        <h3 className={`text-xs font-semibold uppercase tracking-wider ${
          variant === 'attention' ? 'text-red-600' : 'text-muted-foreground'
        }`}>
          {label}
        </h3>
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
          variant === 'attention' ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'
        }`}>
          {count}
        </span>
        <div className={`flex-1 border-t ${variant === 'attention' ? 'border-red-200' : 'border-border'}`} />
      </div>
      {children}
    </div>
  );
}
